import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppState } from '../context/AppStateContext';
import { signOutAndRedirect } from '../api/axios';
import UserProfilePopup from './UserProfilePopup';
import OfflineRibbon from './connectivity/OfflineRibbon';
import SupportAccessIndicator from './SupportAccessIndicator';
import SupportSessionBanner from './SupportSessionBanner';
import { clearSelectedOrgId } from '../services/selectedOrg';
import { exitSupportSession } from '../services/supportDataService';
// import rustleLogoSquare from '../images/rustle_it_logo_square_white.png';

export type LayoutOutletContext = {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

// Where the menu stops being affordable — which depends on the page, because
// the question was never "is the screen small" but "does this page need the
// room more than the menu does".
//
// Most pages: set so a 1366px laptop keeps the menu pinned. A media query
// measures the viewport, which on such a screen reports ~1349 once the
// scrollbar is off, so a threshold of 1366 itself would collapse the menu on
// exactly the machines it's meant to accommodate.
const MENU_FITS_QUERY = '(min-width: 1340px)';

// Build Report is the exception at the other end: it carries two panes and the
// child rail at once. For its preview to sit at full size beside the posts
// column it wants ~1100 of workspace, which with a 250px menu and the 111px
// child rail comes to ~1460. Below that the menu is the cheapest thing to give
// up — losing it hands back 174px, enough to stop the preview being zoomed.
const MENU_FITS_QUERY_REPORTS = '(min-width: 1480px)';
const REPORTS_PATH = '/reports';

// The Dashboard is exempt from all of it. It has no competing panes to give
// space to: no report preview, no posts drawer, no child rail. Collapsing
// there buys room nothing is asking for, on the one screen everybody sees
// first.
const DASHBOARD_PATH = '/';
const onDashboard = (pathname: string) => pathname === DASHBOARD_PATH;

const menuFitsQuery = (pathname: string) =>
  pathname === REPORTS_PATH ? MENU_FITS_QUERY_REPORTS : MENU_FITS_QUERY;

const Layout: React.FC = () => {
  const { user, activeUser, selectedOrganisation, setIsLocked } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();

  // The menu is the first thing to give way when the screen gets tight. Below
  // this the app can't carry it alongside a two-pane workspace — on Reports the
  // preview and the posts/PDF drawer each hold a 500px floor, and with the
  // child rail that already needs ~1100 — so it slides out on its own and
  // slides back when the room returns.
  //
  // Driven by the media query's change event rather than by the current width,
  // so it only acts on crossing: opening the menu by hand on a small screen
  // sticks until the width itself changes, instead of being fought on render.
  const [sidebarOpen, setSidebarOpen] = useState(
    () =>
      typeof window === 'undefined' ||
      onDashboard(window.location.pathname) ||
      window.matchMedia(menuFitsQuery(window.location.pathname)).matches
  );

  // Once the menu is a rail, that is what it stays. Nothing here ever opens it
  // again — not widening the window, not navigating, not returning to the
  // Dashboard. The automatic behaviour can only ever take space back when the
  // screen runs out of it; putting the menu back is the burger's job and the
  // user's decision alone.
  //
  // Re-subscribed per route rather than read through a ref, because the
  // threshold itself is now the thing that changes between pages.
  const widthQuery = menuFitsQuery(location.pathname);
  const isDashboard = onDashboard(location.pathname);
  useEffect(() => {
    const mq = window.matchMedia(widthQuery);
    const onChange = (e: MediaQueryListEvent) => {
      if (!e.matches && !isDashboard) setSidebarOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [widthQuery, isDashboard]);

  const [railHoverBlocked, setRailHoverBlocked] = useState(false);

  // Arriving somewhere that does need the room, on a screen that hasn't got
  // it. Keyed off the destination rather than the transition, so a refresh or
  // a deep link straight into a workspace page collapses just the same.
  //
  // Deliberately one-way: coming back to the Dashboard leaves the menu however
  // you left it. Re-opening it here too would be more symmetrical, but the
  // menu would then visibly move on every hop between Dashboard and Reports,
  // and it would overrule anyone who had collapsed it on purpose.
  useEffect(() => {
    // Every navigation, whatever the destination and whether or not anything
    // collapses: you almost certainly got here by clicking a link in the menu,
    // which leaves the pointer on it and the focus in it — so a rail would
    // float straight back open over the page you just asked for. Drop the
    // focus and hold the hover off until the pointer does something new.
    // (Held separate from the collapse below, which the Dashboard is exempt
    // from — the leftover hover happens on the way there just the same.)
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('.filters-panel')) active.blur();
    setRailHoverBlocked(true);

    if (onDashboard(location.pathname)) return;
    if (window.matchMedia(menuFitsQuery(location.pathname)).matches) return;
    setSidebarOpen(false);
  }, [location.pathname]);

  // The block is only meant to survive the leftover hover from the click that
  // caused the collapse. It lifts on either sign that the pointer is doing
  // something new: leaving the menu, or moving onto a different item from the
  // one just navigated to — which is a deliberate reach for the menu, not the
  // hand still resting where it clicked.
  useEffect(() => {
    if (!railHoverBlocked) return;
    const release = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest('.filters-panel')) {
        setRailHoverBlocked(false);
        return;
      }
      const item = t.closest('.sidebar-nav-item');
      if (item && !item.classList.contains('active')) setRailHoverBlocked(false);
    };
    document.addEventListener('mousemove', release);
    return () => document.removeEventListener('mousemove', release);
  }, [railHoverBlocked]);
  const [showMenu, setShowMenu] = useState(false);
  const userProfileBtnRef = useRef<HTMLDivElement>(null);

  const handleIconClick = () => setShowMenu(prev => !prev);

  const onCloseUserProfilePopup = (event: MouseEvent) => {
    if (event && userProfileBtnRef.current && userProfileBtnRef.current.contains(event.target as Node)) {
      return;
    }
    setShowMenu(false);
  };

  const onLogout = async () => {
    try {
      clearSelectedOrgId({ forgetLastUsed: true });
      // Routes through the shared helper so the active-user token is cleared
      // before we redirect. Without that, /login re-hydrates activeUser from the
      // leftover token, the session-expiry guard re-arms, finds no Cognito
      // session, and wrongly shows "Your session has expired" on a manual logout.
      // No reason is passed, so no notice is stashed.
      await signOutAndRedirect();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const onChangePassword = () => {
    setShowMenu(false);
    navigate('/change-password');
  };

  const onLockDevice = () => {
    setShowMenu(false);
    // Support has no PIN to unlock with, so "lock" ends the session instead.
    if (activeUser?.isSupport) { exitSupportSession(); return; }
    setIsLocked(true);
  };

  return (
    <div className={`layout-container${railHoverBlocked ? ' rail-hover-blocked' : ''}`}>
      <div className="layout-fixed-header d-flex justify-content-between align-items-center px-3">
        <div className="d-flex align-items-center gap-4">
          {/* A pin, not a show/hide: the menu is never gone, it's either held
              open or collapsed to the icon rail. Names the state it will put
              you in, not the one you're in. */}
          <button
            className={`btn p-0 border-0 ${sidebarOpen ? 'burger-selected' : 'text-white'}`}
            style={{ fontSize: '1.6rem' }}
            onClick={() => {
              // The only control that opens the menu. Collapsing through it
              // skips the anti-flap timings, same as a navigation-driven one.
              if (sidebarOpen) setRailHoverBlocked(true);
              setSidebarOpen(prev => !prev);
            }}
            title={sidebarOpen ? 'Collapse the menu to icons' : 'Pin the menu open'}
            aria-label={sidebarOpen ? 'Collapse the menu to icons' : 'Pin the menu open'}
            aria-pressed={sidebarOpen}
          >
            <i className="fas fa-bars"></i>
          </button>
          <div className="d-flex align-items-center gap-2">
            {/* <img src={rustleLogoSquare} alt="Rustle it logo" style={{ height: '28px', width: 'auto' }} /> */}
            <span className="text-white h5 mb-0">Rustle It<sup className="reg">®</sup></span>
          </div>
          {selectedOrganisation && (
            <div className="d-flex align-items-center gap-2" title={selectedOrganisation.name}>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 300 }}>|</span>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 500, letterSpacing: '0.01em' }}>
                {selectedOrganisation.name}
              </span>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-3">
          <SupportAccessIndicator />
          <SupportSessionBanner />
          {/* Support is not staff: there is no one to switch to. */}
          {activeUser && !activeUser.isSupport && (
            <button
              onClick={() => navigate('/staff-select')}
              className="btn btn-sm d-flex align-items-center gap-2"
              data-help="account.switch-pill"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, color: '#fff', padding: '0.25rem 0.75rem' }}
              title="Switch user"
            >
              <div
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: activeUser.colourCode,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.65rem', fontWeight: 700, color: '#fff', flexShrink: 0,
                }}
              >
                {activeUser.initials}
              </div>
              <span style={{ fontSize: '0.85rem' }}>{activeUser.name}</span>
              <i className="fas fa-exchange-alt" style={{ fontSize: '0.75rem', opacity: 0.7 }}></i>
            </button>
          )}
          <div ref={userProfileBtnRef} style={{ position: 'relative' }} data-help="account.profile-icon">
            <i className="fas fa-user-circle user-profile-icon" onClick={handleIconClick}></i>
          </div>
        </div>
      </div>

      {user && (
        <UserProfilePopup
          user={user}
          activeUser={activeUser}
          showMenu={showMenu}
          onChangePassword={onChangePassword}
          onLockDevice={onLockDevice}
          onClose={onCloseUserProfilePopup}
          onLogout={onLogout}
        />
      )}

      <OfflineRibbon placement="inline" />

      <div className="layout-main" style={{ display: 'flex' }}>
        <Outlet context={{ sidebarOpen, setSidebarOpen } satisfies LayoutOutletContext} />
      </div>
    </div>
  );
};

export default Layout;
